import {
  AlertTriangle,
  BarChart3,
  BookOpen,
  HandHeart,
  MessageSquare,
  Mic,
  ThumbsUp,
  type LucideIcon,
} from "lucide-react";
import type { CsStats, CsUnmatchedQuestion } from "../../api/aiKefuService";

export type SummaryCard = {
  /** i18n key under `customer-service:summary.*` (e.g. "todaySessionCount") */
  labelKey: string;
  value: string;
  /** 可选的子标题，展示分子/分母来源，避免率化指标脱离上下文 */
  hint?: string;
  icon: LucideIcon;
  color: string;
};

/** 后端率化字段分母 0 时返回 null；前端统一格式化成 "—" 而不是 NaN%。 */
function formatRate(rate: number | null): string {
  if (rate === null || rate === undefined || !Number.isFinite(rate)) return "—";
  return `${(rate * 100).toFixed(1)}%`;
}

export type BuildSummaryT = (key: string, opts?: Record<string, unknown>) => string;

/**
 * Build summary cards from stats. `t` lets the caller produce localized
 * `hint` strings while keeping pure-data label keys for the consumer to render.
 */
export function buildSummaryCards(stats: CsStats | undefined, t?: BuildSummaryT): SummaryCard[] {
  const hint = (key: string, vars: Record<string, unknown>) =>
    t
      ? t(key, vars)
      : Object.entries(vars).reduce((s, [k, v]) => s.replace(`{{${k}}}`, String(v)), key);
  return [
    {
      labelKey: "todaySessionCount",
      value: stats ? String(stats.todaySessionCount) : "-",
      icon: MessageSquare,
      color: "bg-primary/10 text-primary",
    },
    {
      labelKey: "totalSessionCount",
      value: stats ? String(stats.totalSessionCount) : "-",
      icon: BarChart3,
      color: "bg-success/10 text-success",
    },
    {
      labelKey: "activeSessionCount",
      value: stats ? String(stats.activeSessionCount) : "-",
      icon: Mic,
      color: "bg-info/10 text-info",
    },
    {
      labelKey: "knowledgeDocCount",
      value: stats
        ? hint("summary.knowledgeDocHint", {
            docs: stats.knowledgeDocCount,
            chunks: stats.totalChunks,
          })
        : "-",
      icon: BookOpen,
      color: "bg-warning/10 text-warning",
    },
    {
      labelKey: "unmatchedQuestionCount",
      value: stats ? String(stats.unmatchedQuestionCount) : "-",
      icon: AlertTriangle,
      color: "bg-destructive/10 text-destructive",
    },
    {
      labelKey: "todayHumanTakeoverRate",
      value: formatRate(stats?.todayHumanTakeoverRate ?? null),
      hint: stats
        ? hint("summary.todayHumanTakeoverHint", {
            handling: stats.humanHandlingCount,
            total: stats.todaySessionCount,
          })
        : undefined,
      icon: HandHeart,
      color: "bg-warning/10 text-warning",
    },
    {
      labelKey: "todayUnmatchedRate",
      value: formatRate(stats?.todayUnmatchedRate ?? null),
      hint: stats
        ? hint("summary.todayUnmatchedHint", {
            unmatched: stats.todayUnmatchedCount,
            assistant: stats.todayAssistantMessageCount,
          })
        : undefined,
      icon: AlertTriangle,
      color: "bg-destructive/10 text-destructive",
    },
    {
      labelKey: "todayPositiveFeedbackRate",
      value: formatRate(stats?.todayPositiveFeedbackRate ?? null),
      hint: stats
        ? hint("summary.todayPositiveFeedbackHint", {
            positive: stats.todayFeedbackPositive,
            negative: stats.todayFeedbackNegative,
          })
        : undefined,
      icon: ThumbsUp,
      color: "bg-success/10 text-success",
    },
  ];
}

export type SuggestionItem = {
  /** 派生自的 CsUnmatchedQuestion.id —— 让 QuickAddKbDialog 提交后能把对应问题标 resolved */
  id: number;
  topic: string;
  source: string;
  urgency: string;
  reason: string;
};

/**
 * 后端 CsUnmatchedQuestion.source 的两类来源（与 UnmatchedQuestionSource 枚举保持一致）：
 *  - wecom     → 企微客服渠道兜底
 *  - ai_answer → AI 回答未命中(渠道无关:网页 / H5、直连 API 等)
 * 1010 一期补完:后端带了会话渠道(channel)时优先按渠道显示「企微客服 / 网页 / H5」;都没有回退到"未命中问题"。
 */
const SOURCE_LABEL_KEYS: Record<string, string> = {
  wecom: "optimize.sourceWecom",
  ai_answer: "optimize.sourceAiAnswer",
};

const UNMATCHED_CHANNEL_KEYS: Record<string, string> = {
  wecom_kf: "channel.wecomKf",
  wecom: "channel.wecomKf",
  web_widget: "channel.webWidget",
};

export function formatSourceLabel(
  q: { source?: string | null; channel?: string | null },
  t?: BuildSummaryT
): string {
  const fallback = "optimize.sourceFallback";
  const key =
    (q.channel ? UNMATCHED_CHANNEL_KEYS[q.channel] : undefined) ??
    (q.source ? (SOURCE_LABEL_KEYS[q.source] ?? fallback) : fallback);
  return t ? t(key) : key;
}

/**
 * 切 Tab 时保留 q/status/page 等筛选参数。
 * 直接 setSearchParams({tab: v}) 会抹掉所有其他参数。
 */
export function mergeSearchParamsWithTab(current: URLSearchParams, tab: string): URLSearchParams {
  const next = new URLSearchParams(current);
  next.set("tab", tab);
  return next;
}

export function buildSuggestions(
  questions: CsUnmatchedQuestion[],
  t?: BuildSummaryT
): SuggestionItem[] {
  const urgency = (count: number) => {
    const key =
      count >= 15
        ? "optimize.urgencyHigh"
        : count >= 8
          ? "optimize.urgencyMedium"
          : "optimize.urgencyLow";
    return t ? t(key) : key;
  };
  const reason = (count: number) =>
    t ? t("optimize.reasonTemplate", { count }) : `optimize.reasonTemplate{count=${count}}`;
  return questions
    .filter((q) => q.status === "open")
    .sort((a, b) => b.hitCount - a.hitCount)
    .slice(0, 5)
    .map((q) => ({
      id: q.id,
      topic: q.question,
      source: formatSourceLabel(q, t),
      urgency: urgency(q.hitCount),
      reason: reason(q.hitCount),
    }));
}
